import {
  Building2,
  IdCard,
  KeyRound,
  LayoutDashboard,
  Lightbulb,
  Megaphone,
  Network,
  NotebookPen,
  ShieldCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';

import type { NavigableViewKey, ViewKey } from './views';

export interface NavigationEntry {
  view: NavigableViewKey;
  icon: ReactNode;
  priority?: number;
}

export const workspaces: Record<string, NavigationEntry[]> = {
  EXECUTIVE: [
    { view: 'dashboard', icon: <LayoutDashboard className="size-4" aria-hidden /> },
    { view: 'sites', icon: <Building2 className="size-4" aria-hidden /> },
    { view: 'departments', icon: <Network className="size-4" aria-hidden /> },
    { view: 'positions', icon: <Users className="size-4" aria-hidden /> },
    { view: 'quickPicks', icon: <Lightbulb className="size-4" aria-hidden /> },
    { view: 'themes', icon: <NotebookPen className="size-4" aria-hidden /> },
    { view: 'versionReview', icon: <ShieldCheck className="size-4" aria-hidden /> },
    { view: 'campaigns', icon: <Megaphone className="size-4" aria-hidden /> },
    { view: 'campaignTracking', icon: <LayoutDashboard className="size-4" aria-hidden /> },
    { view: 'myCampaigns', icon: <Megaphone className="size-4" aria-hidden /> },
    { view: 'employees', icon: <IdCard className="size-4" aria-hidden /> },
    { view: 'staffingRequests', icon: <UserPlus className="size-4" aria-hidden /> },
  ],
  HR: [
    { view: 'departments', icon: <Network className="size-4" aria-hidden /> },
    { view: 'positions', icon: <Users className="size-4" aria-hidden /> },
    { view: 'quickPicks', icon: <Lightbulb className="size-4" aria-hidden /> },
    { view: 'themes', icon: <NotebookPen className="size-4" aria-hidden /> },
    { view: 'versionReview', icon: <ShieldCheck className="size-4" aria-hidden /> },
    { view: 'employees', icon: <IdCard className="size-4" aria-hidden /> },
    { view: 'campaigns', icon: <Megaphone className="size-4" aria-hidden /> },
    { view: 'roles', icon: <ShieldCheck className="size-4" aria-hidden /> },
    { view: 'permissions', icon: <KeyRound className="size-4" aria-hidden /> },
    { view: 'staffingRequests', icon: <UserPlus className="size-4" aria-hidden /> },
  ],
  MANAGER: [
    { view: 'sites', icon: <Building2 className="size-4" aria-hidden /> },
    { view: 'departments', icon: <Network className="size-4" aria-hidden /> },
    { view: 'positions', icon: <Users className="size-4" aria-hidden /> },
    { view: 'quickPicks', icon: <Lightbulb className="size-4" aria-hidden /> },
    { view: 'themes', icon: <NotebookPen className="size-4" aria-hidden /> },
    { view: 'versionReview', icon: <ShieldCheck className="size-4" aria-hidden /> },
    { view: 'campaigns', icon: <Megaphone className="size-4" aria-hidden /> },
    { view: 'myCampaigns', icon: <Megaphone className="size-4" aria-hidden /> },
    { view: 'employees', icon: <IdCard className="size-4" aria-hidden /> },
    { view: 'staffingRequests', icon: <UserPlus className="size-4" aria-hidden /> },
  ],
  EMPLOYEE: [{ view: 'myCampaigns', icon: <Megaphone className="size-4" aria-hidden /> }],
};

export const mobileWorkspaces: Record<string, NavigationEntry[]> = {
  EXECUTIVE: [
    { view: 'campaigns', icon: <Megaphone />, priority: 50 },
    { view: 'myCampaigns', icon: <Megaphone />, priority: 50 },
    { view: 'employees', icon: <IdCard />, priority: 40 },
    { view: 'sites', icon: <Building2 />, priority: 30 },
    { view: 'departments', icon: <Network />, priority: 20 },
    { view: 'positions', icon: <Users />, priority: 15 },
    { view: 'themes', icon: <NotebookPen />, priority: 10 },
    { view: 'staffingRequests', icon: <UserPlus />, priority: 10 },
  ],
  HR: [
    { view: 'campaigns', icon: <Megaphone />, priority: 50 },
    { view: 'employees', icon: <IdCard />, priority: 40 },
    { view: 'departments', icon: <Network />, priority: 20 },
    { view: 'positions', icon: <Users />, priority: 15 },
    { view: 'themes', icon: <NotebookPen />, priority: 10 },
    { view: 'roles', icon: <ShieldCheck />, priority: 5 },
    { view: 'permissions', icon: <KeyRound />, priority: 5 },
    { view: 'staffingRequests', icon: <UserPlus />, priority: 5 },
  ],
  MANAGER: [
    { view: 'campaigns', icon: <Megaphone />, priority: 50 },
    { view: 'employees', icon: <IdCard />, priority: 40 },
    { view: 'sites', icon: <Building2 />, priority: 30 },
    { view: 'departments', icon: <Network />, priority: 20 },
    { view: 'positions', icon: <Users />, priority: 15 },
    { view: 'themes', icon: <NotebookPen />, priority: 10 },
    { view: 'staffingRequests', icon: <UserPlus />, priority: 10 },
  ],
  EMPLOYEE: [{ view: 'myCampaigns', icon: <Megaphone />, priority: 50 }],
};

export function unionWorkspaceEntries(
  source: Record<string, NavigationEntry[]>,
  roles: string[],
): NavigationEntry[] {
  const seen = new Set<ViewKey>();
  return roles
    .flatMap((role) => source[role] ?? [])
    .filter((entry) => {
      if (seen.has(entry.view)) return false;
      seen.add(entry.view);
      return true;
    });
}
